import { isWithinBufferRangeOfTarget } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { debounce, omit } from "es-toolkit/compat";
import { nanoid } from "nanoid";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HotkeysProvider } from "react-hotkeys-hook";
import { EmptyState } from "../EmptyState";
import { Icon, IconLoading } from "../Icon";
import { Pagination } from "../Pagination";
import { DefaultPageSizeOptions } from "../Pagination/Pagination.constants";
import type { SortingType } from "../utils/component-props";
import { DataRow } from "./DataRow/DataRow";
import { getActionButtonSize } from "./DataTable.helpers";
import styles from "./DataTable.module.scss";
import { DataTableEmptyState } from "./DataTableEmptyState";
import { HeaderRow } from "./HeaderRow/HeaderRow";
import { SelectionActionBanner } from "./SelectionActionBanner";
import type {
  DataRowProps,
  DataTableProps,
  HeaderType,
  ItemBaseType,
} from "./types";

const cx = classNames.bind(styles);

/**
 * Used to show tabular data
 */
const DataTable = <ItemType extends ItemBaseType>({
  headers,
  items,
  selected,
  onSelectionChange,
  isSelectionInverted: isSelectionInvertedProp,
  isSingleSelection = false,
  hideSelectionCheckboxes = false,
  totalSelectableItems,
  pagination = {
    page: 1,
    pageSize: 25,
    pageSizeText: "rows per page",
  },
  onPaginationChange: onPaginationChangeCallback,
  sorting = {
    sortBy: headers[0]?.key,
    sortDirection: "asc",
  },
  onSortChange: onSortChangeCallback,
  onRowClick,
  onHeaderClick,
  isLoading = false,
  isBusy = false,
  density = "normal",
  intent = "data-first",
  maxHeight,
  getItemActions,
  getItemBulkActions,
  isFluid = true,
  emptyStateProps = {},
  getItemId,
  showRowNumbers,
  rowNumberDirection = "forward",
  getRowAdornment,
  getRowIndicatorStyle,
}: DataTableProps<ItemType>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const tableHeadRef = useRef<HTMLTableSectionElement>(null);
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const [tableRowActionsRightPosition, setTableRowActionsRightPosition] =
    useState<number | null>(null);
  const [tableRowActionsMaxWidth, setTableRowActionsMaxWidth] =
    useState<number>(80);
  const [cellLastFixedPos, setCellLastFixedPos] = useState<number>(0);
  const scrollAffordancePosBuffer = useRef(25);
  const [scrollLeftAffordancePos, setScrollLeftAffordancePos] = useState<
    number | null
  >(null);
  const [scrollRightAffordancePos, setScrollRightAffordancePos] = useState<
    number | null
  >(null);
  const [isSelectionInvertedInternal, setIsSelectionInverted] = useState(
    isSelectionInvertedProp ?? false
  );
  const isSelectionInverted =
    isSelectionInvertedProp ?? isSelectionInvertedInternal;

  const isSelectionEnabled =
    selected !== undefined && typeof getItemId === "function";
  const isRowIndicatorEnabled =
    (!hideSelectionCheckboxes && isSelectionEnabled) || showRowNumbers;

  const onPaginationChange: DataTableProps<ItemType>["onPaginationChange"] = (
    newPagination
  ) => {
    if (onPaginationChangeCallback) {
      onPaginationChangeCallback(newPagination);
    }
  };

  const onSortChange = (newSorting: SortingType) => {
    onSortChangeCallback?.(newSorting);
  };

  const routeSelectionChange: DataTableProps<ItemType>["onSelectionChange"] = (
    selected,
    shouldInvertSelection
  ) => {
    onSelectionChange?.(selected, shouldInvertSelection);
    setIsSelectionInverted(shouldInvertSelection ?? false);
  };

  const handleSelectionChange: DataRowProps<ItemType>["onChangeSelection"] =
    useCallback(
      (selectedItem, { isAlreadySelected, selectAll }) => {
        if (!isSelectionEnabled) return;
        if (isSelectionInverted) {
          routeSelectionChange(
            Object.fromEntries(
              items
                .filter((item) => getItemId(item) !== getItemId(selectedItem))
                .map((item) => [getItemId(item), item])
            ),
            false
          );
          return;
        }

        if (selectAll) {
          const isEveryItemOnPageSelected = items.every(
            (item) => !!selected?.[getItemId(item)]
          );

          routeSelectionChange(
            isEveryItemOnPageSelected
              ? omit(selected, items.map(getItemId))
              : Object.fromEntries(
                  Object.values(selected)
                    .concat(items)
                    .map((item) => [getItemId(item), item])
                ),
            false
          );
          return;
        }

        if (isSingleSelection) {
          routeSelectionChange(
            isAlreadySelected
              ? {}
              : {
                  [getItemId(selectedItem)]: selectedItem,
                },
            false
          );
        } else {
          routeSelectionChange(
            isAlreadySelected
              ? omit(selected, getItemId(selectedItem))
              : { ...selected, [getItemId(selectedItem)]: selectedItem },
            false
          );
        }
      },
      [
        isSelectionEnabled,
        routeSelectionChange,
        selected,
        getItemId,
        isSingleSelection,
      ]
    );

  const handleScrollAffordanceClick = (dir: "left" | "right") => {
    if (!tableContainerRef.current) return;

    // actual scrollable area is the full scroll width minus the client width
    // (the viewable area) but we're just going to do half of the visible area
    const halfwayOfViewportScrollWidth =
      tableContainerRef.current?.clientWidth * 0.5;

    if (dir === "left") {
      tableContainerRef.current.scrollTo({
        left: !isWithinBufferRangeOfTarget(
          tableContainerRef.current.scrollLeft,
          {
            target: 0,
            buffer: scrollAffordancePosBuffer.current,
            inclusive: true,
          }
        )
          ? tableContainerRef.current.scrollLeft - halfwayOfViewportScrollWidth
          : 0,
        behavior: "smooth",
      });
    } else {
      tableContainerRef.current.scrollTo({
        left: !isWithinBufferRangeOfTarget(
          tableContainerRef.current.scrollLeft,
          {
            target:
              tableContainerRef.current.scrollWidth -
              tableContainerRef.current.clientWidth,
            buffer: scrollAffordancePosBuffer.current,
            inclusive: true,
          }
        )
          ? tableContainerRef.current.scrollLeft + halfwayOfViewportScrollWidth
          : tableContainerRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  };

  const positionTableRowActionsAtVisibleRightEdge = useCallback(() => {
    const tableDimensions = tableRef.current?.getBoundingClientRect();
    const tableContainerDimensions =
      tableContainerRef.current?.getBoundingClientRect();

    if (
      !tableDimensions ||
      !tableContainerDimensions ||
      !tableContainerRef.current
    )
      return;

    const newRightPos =
      tableDimensions.width -
      tableContainerDimensions.width -
      tableContainerRef.current.scrollLeft;

    setTableRowActionsRightPosition(newRightPos < 0 ? 0 : newRightPos);
  }, [tableRowActionsRightPosition]);

  const positionTableScrollAffordances = useCallback(() => {
    const tableDimensions = tableRef.current?.getBoundingClientRect();
    const tableContainerDimensions =
      tableContainerRef.current?.getBoundingClientRect();

    if (
      !tableDimensions ||
      !tableContainerDimensions ||
      !tableContainerRef.current
    )
      return;

    const newRightPos =
      tableDimensions.width -
      tableContainerDimensions.width -
      tableContainerRef.current.scrollLeft;
    const newLeftPos = tableContainerRef.current.scrollLeft + cellLastFixedPos;

    setScrollRightAffordancePos(
      newRightPos < scrollAffordancePosBuffer.current ? 0 : newRightPos
    );
    setScrollLeftAffordancePos(
      newLeftPos < scrollAffordancePosBuffer.current
        ? cellLastFixedPos
        : newLeftPos
    );
  }, [cellLastFixedPos]);

  const debouncedPositionTableScrollAffordances = useCallback(
    debounce(() => {
      requestAnimationFrame(() => {
        positionTableScrollAffordances();
      });
    }, 1000),
    [positionTableScrollAffordances]
  );

  const debouncedPositionStickyUI = useCallback(
    debounce(() => {
      requestAnimationFrame(() => {
        positionTableRowActionsAtVisibleRightEdge();
        positionTableScrollAffordances();
      });
    }, 1000),
    [positionTableRowActionsAtVisibleRightEdge, positionTableScrollAffordances]
  );
  const resizeObserver = useMemo(
    () => new ResizeObserver(debouncedPositionStickyUI),
    [debouncedPositionStickyUI]
  );

  // handle scroll
  useEffect(() => {
    if (isLoading) return;

    const listener = () => {
      // hide all sticky affordances while repositioning happens, for performance
      setScrollLeftAffordancePos(null);
      setScrollRightAffordancePos(null);
      setTableRowActionsRightPosition(null);
      // reposition the UI
      debouncedPositionStickyUI();
    };

    tableContainerRef.current?.removeEventListener("scroll", listener);
    tableContainerRef.current?.addEventListener("scroll", listener);
    if (tableContainerRef.current)
      resizeObserver.observe(tableContainerRef.current);

    return () => {
      tableContainerRef.current?.removeEventListener("scroll", listener);
      resizeObserver.disconnect();
    };
  }, [tableRowActionsRightPosition, resizeObserver, isLoading]);

  // handle fixed cell change
  useEffect(() => {
    if (isLoading) return;

    debouncedPositionTableScrollAffordances();
  }, [cellLastFixedPos, isLoading]);

  // initial render
  useEffect(() => {
    if (isLoading) return;

    positionTableRowActionsAtVisibleRightEdge();
    positionTableScrollAffordances();
  }, [isLoading]);

  const headerGroups = useMemo(() => {
    const headerSpans: Record<string, number> = {};
    let traversalPath: Array<HeaderType<ItemType>> = [];

    const updateTraversalPath = (
      depth: number,
      newHeader: HeaderType<ItemType>
    ) => {
      const updateTraversalPathHistory = () => {
        traversalPath.forEach((segment, idx) => {
          if (idx === traversalPath.length - 1) return;
          if (typeof headerSpans[segment.key] === "undefined") {
            headerSpans[segment.key] = 1;
            return;
          }

          headerSpans[segment.key] += 1;
        });
      };

      const isLeaf = !newHeader.headers;

      if (depth >= traversalPath.length) {
        traversalPath.push(newHeader);
        if (isLeaf) updateTraversalPathHistory();
        return;
      }

      // update at depth
      traversalPath[depth] = newHeader;
      // clear everything below by picking off the slice of path up to the current depth
      traversalPath = traversalPath.slice(0, depth + 1);
      // update everything that came before in the path, if this is the end of the path
      if (isLeaf) updateTraversalPathHistory();
    };

    const getDepth = (header: HeaderType<ItemType>, depth = 0): number => {
      updateTraversalPath(depth, header);

      if (!header.headers) {
        return depth;
      }

      return header.headers.reduce<number>((largestDepth, header) => {
        const headerDepth = getDepth(header, depth + 1);

        return Math.max(largestDepth, headerDepth);
      }, depth);
    };

    const deepestDepth = headers.length
      ? Math.max(...headers.map((header) => getDepth(header, 0)))
      : 0;
    const columnGroups = new Array(deepestDepth + 1).fill(null).map<{
      key: string;
      columns: Array<HeaderType<ItemType>>;
    }>((_, idx, arr) => ({ key: nanoid(), columns: idx !== arr.length - 1 && isRowIndicatorEnabled ? [{ key: nanoid(), isFixed: true, isDefaultFixed: true, label: "" }] : [] }));

    const evaluateHeader = (
      header: HeaderType<ItemType>,
      checkingDepth: number
    ) => {
      if (header.headers || checkingDepth === deepestDepth) {
        columnGroups[checkingDepth].columns.push({
          ...header,
          span: headerSpans[header.key] ?? header.headers?.length ?? 1,
        });
        return;
      }

      // push holes and push the header down to the deepest depth
      for (
        let insertionDepth = checkingDepth;
        insertionDepth < deepestDepth;
        insertionDepth++
      ) {
        columnGroups[insertionDepth].columns.push({
          key: nanoid(),
          span: 1,
          // if the leaf header is fixed, the holes will be fixed too
          isFixed: header.isFixed,
          label: "",
        });
      }

      columnGroups[deepestDepth].columns.push({
        ...header,
        span: 1,
      });
    };

    const evaluateHeaders = (
      headers: Array<HeaderType<ItemType>>,
      checkingDepth = 0
    ) => {
      for (const header of headers) {
        evaluateHeader(header, checkingDepth);

        if (header.headers) {
          evaluateHeaders(header.headers, checkingDepth + 1);
        }
      }
    };

    evaluateHeaders(headers);
    return columnGroups;
  }, [headers]);

  const headerCheckboxStatus = useMemo(() => {
    if (!isSelectionEnabled || !items?.length)
      return { value: false, isIndeterminate: false };

    const numSelectedItemsOnPage = items.filter(
      (item) => !!selected?.[getItemId(item)]
    ).length;

    return {
      value: numSelectedItemsOnPage > 0 || !!isSelectionInverted,
      isIndeterminate:
        numSelectedItemsOnPage > 0 && numSelectedItemsOnPage !== items.length,
    };
  }, [items, selected, isSelectionEnabled, isSelectionInverted]);

  const displayColumns = useMemo<Array<HeaderType<ItemType>>>(() => {
    return headerGroups
      .slice()
      [
        headerGroups.length - 1
      ].columns.sort(({ isFixed: aFixed }, { isFixed: bFixed }) => (aFixed && !bFixed ? -1 : 1));
  }, [headerGroups, items]);

  const shouldLeafCellsBreakMaxWidthCeiling = useMemo<boolean>(() => {
    if (!displayColumns?.length || !tableContainerRef.current) return false;

    return (
      displayColumns.length * 395 <
      tableContainerRef.current.getBoundingClientRect().width
    );
  }, [
    displayColumns,
    tableContainerRef.current?.getBoundingClientRect().width,
  ]);

  const displayRows = useMemo(
    () =>
      (isLoading && !items?.length
        ? Array.from(Array(1).keys()).map(() => null)
        : items
      ).map((item, index) => (
        <DataRow
          key={item ? getItemId?.(item) || index : index}
          rowIndex={index}
          showRowNumbers={showRowNumbers}
          rowNumberDirection={rowNumberDirection}
          getRowAdornment={getRowAdornment}
          getRowIndicatorStyle={getRowIndicatorStyle}
          items={items}
          item={item}
          selected={selected}
          isSelectionInverted={isSelectionInverted}
          isSelectionEnabled={isSelectionEnabled}
          isSingleSelection={isSingleSelection}
          onChangeSelection={handleSelectionChange}
          hideSelectionCheckboxes={hideSelectionCheckboxes}
          onRowClick={onRowClick}
          headers={headers}
          leafHeaders={displayColumns}
          tableRowActionsRightPosition={tableRowActionsRightPosition}
          tableRowActionsMaxWidth={tableRowActionsMaxWidth}
          setTableRowActionsMaxWidth={setTableRowActionsMaxWidth}
          shouldLeafCellsBreakMaxWidthCeiling={
            shouldLeafCellsBreakMaxWidthCeiling
          }
          isLoading={isLoading}
          isBusy={isBusy}
          getItemActions={getItemActions}
          getItemId={getItemId}
          density={density}
          intent={intent}
          pagination={pagination}
          sorting={sorting}
        />
      )),
    [
      isLoading,
      isBusy,
      items,
      selected,
      isSelectionInverted,
      isSelectionEnabled,
      isSingleSelection,
      hideSelectionCheckboxes,
      headers,
      displayColumns,
      tableRowActionsRightPosition,
      tableRowActionsMaxWidth,
      getItemActions,
      getItemId,
      density,
      intent,
      onRowClick,
      showRowNumbers,
      getRowAdornment,
      getRowIndicatorStyle,
      shouldLeafCellsBreakMaxWidthCeiling,
      handleSelectionChange,
      pagination,
      sorting,
      rowNumberDirection,
    ]
  );

  const itemBulkActions = useMemo(
    () =>
      getItemBulkActions?.({
        selected: selected ?? {},
        isSelectionInverted,
        buttonProps: {
          size: getActionButtonSize(density),
        },
      }) ?? [],
    [selected, density, getItemBulkActions, isSelectionInverted]
  );
  const scrollLeftAffordancePosition =
    scrollLeftAffordancePos !== null &&
    scrollLeftAffordancePos >
      cellLastFixedPos + scrollAffordancePosBuffer.current
      ? scrollLeftAffordancePos
      : null;
  const scrollRightAffordancePosition =
    scrollRightAffordancePos !== null &&
    scrollRightAffordancePos >= scrollAffordancePosBuffer.current
      ? scrollRightAffordancePos
      : null;

  const showPagination =
    pagination?.page !== undefined &&
    pagination?.pageSize !== undefined &&
    ((pagination?.total &&
      pagination.total >
        Math.min(DefaultPageSizeOptions[0], pagination.pageSize)) ||
      pagination?.total === undefined ||
      pagination?.total === 0);

  if (!isLoading && !headers?.length && items.length) {
    return (
      <div className={cx("container", { isFluid }, "empty")}>
        <EmptyState
          icon={<Icon variant="wrench" />}
          title="Columns not configured for table"
          suggestion="Configure visible columns to see data"
        />
      </div>
    );
  }

  if (!isLoading && !headers.length && !items.length) {
    return (
      <div className={cx("container", { isFluid }, "empty")}>
        <DataTableEmptyState
          isFluid={isFluid}
          {...emptyStateProps}
        />
      </div>
    );
  }

  return (
    <HotkeysProvider initiallyActiveScopes={["rows"]}>
      <div
        className={cx("container", {
          isFluid,
          isScrollable:
            tableRef.current &&
            tableContainerRef.current &&
            tableRef.current?.clientWidth >
              tableContainerRef.current?.clientWidth,
        })}
        style={{ ...(maxHeight !== undefined ? { maxHeight } : {}) }}
        ref={tableContainerRef}
      >
        <table
          className={cx("table")}
          ref={tableRef}
          aria-busy={isLoading}
        >
          <thead ref={tableHeadRef}>
            <HeaderRow
              tableRowActionsMaxWidth={tableRowActionsMaxWidth}
              shouldLeafCellsBreakMaxWidthCeiling={
                shouldLeafCellsBreakMaxWidthCeiling
              }
              isScrolledVertically={
                !!(
                  tableContainerRef.current &&
                  tableContainerRef.current?.scrollTop > 0
                )
              }
              showRowNumbers={showRowNumbers}
              isSticky={maxHeight !== undefined}
              onLastFixedCellPositionChange={setCellLastFixedPos}
              scrollLeftAffordancePosition={scrollLeftAffordancePosition}
              scrollRightAffordancePosition={scrollRightAffordancePosition}
              handleScrollAffordanceClick={handleScrollAffordanceClick}
              sorting={sorting}
              headers={headers}
              headerGroups={headerGroups}
              hasRowActions={!!getItemActions}
              isSelectionEnabled={isSelectionEnabled}
              isSingleSelection={isSingleSelection}
              hideSelectionCheckboxes={hideSelectionCheckboxes}
              onSelectAll={() =>
                handleSelectionChange(null, { selectAll: true })
              }
              selectedStatus={headerCheckboxStatus}
              onSortChange={onSortChangeCallback ? onSortChange : undefined}
              isLoading={isLoading}
              items={items}
              density={density}
              intent={intent}
              onHeaderClick={onHeaderClick}
              selectionBar={
                isSelectionEnabled &&
                !isLoading &&
                (Object.keys(selected).length || isSelectionInverted)
                  ? (selectionCheckboxWidth) => (
                      <SelectionActionBanner
                        selectedCount={
                          isSelectionInverted
                            ? (totalSelectableItems ??
                                pagination.total ??
                                items?.length ??
                                0) - Object.keys(selected).length
                            : Object.keys(selected).length
                        }
                        totalCount={
                          totalSelectableItems ??
                          pagination.total ??
                          items?.length ??
                          0
                        }
                        isAllSelected={isSelectionInverted}
                        isAllSelectionEnabled={
                          (totalSelectableItems !== undefined &&
                            totalSelectableItems <= items.length) ||
                          (totalSelectableItems === undefined &&
                            (pagination.total !== undefined ||
                              items.length !== undefined))
                        }
                        onSelectionAllChange={(isAllSelected) =>
                          routeSelectionChange({}, !!isAllSelected)
                        }
                        actions={itemBulkActions}
                        visibleTableWidth={
                          tableContainerRef.current?.clientWidth ?? 0
                        }
                        selectionCheckboxWidth={selectionCheckboxWidth}
                      />
                    )
                  : undefined
              }
            />
          </thead>
          {isLoading || items?.length ? (
            <tbody ref={tableBodyRef}>{displayRows}</tbody>
          ) : null}
        </table>
        {!isLoading && !items?.length ? (
          <DataTableEmptyState
            isFluid={isFluid}
            {...emptyStateProps}
          />
        ) : null}
      </div>

      <div className={cx("footer", !showPagination && "no-pagination")}>
        <div style={{ flexGrow: 1 }}>
          {isBusy ? <IconLoading speed="fast" /> : null}
        </div>
        <div>
          {showPagination ? (
            <Pagination
              {...pagination}
              pageTotal={items?.length ?? 0}
              sortDirection={sorting.sortDirection}
              isLoading={isLoading}
              isBusy={isBusy}
              onChange={onPaginationChange}
            />
          ) : null}
        </div>
      </div>
    </HotkeysProvider>
  );
};

DataTable.DataRow = DataRow;

export default DataTable;
